import React, { useState, useEffect } from 'react';
import { Users, Key, Ticket, TrendingUp, Activity, AlertCircle } from 'lucide-react';
import { adminAPI } from '../../services/api';
import './AdminDashboard.css';

interface Stats {
  total_users: number;
  total_api_keys: number;
  active_keys: number;
  total_redeem_codes: number;
  unused_codes: number;
  total_requests_today: number;
  total_revenue: number;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <AlertCircle size={20} />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="page-header">
        <div>
          <h1>Admin Dashboard</h1>
          <p>System overview and management</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4">
        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">Total Users</span>
            <div className="stats-card-icon" style={{ background: '#dbeafe' }}>
              <Users size={20} style={{ color: '#2563eb' }} />
            </div>
          </div>
          <div className="stats-card-value">{stats?.total_users || 0}</div>
          <div className="stats-card-change positive">
            <TrendingUp size={16} />
            Active platform users
          </div>
        </div>

        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">API Keys</span>
            <div className="stats-card-icon" style={{ background: '#dcfce7' }}>
              <Key size={20} style={{ color: '#16a34a' }} />
            </div>
          </div>
          <div className="stats-card-value">{stats?.total_api_keys || 0}</div>
          <div className="stats-card-change">
            {stats?.active_keys || 0} active
          </div>
        </div>

        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">Redeem Codes</span>
            <div className="stats-card-icon" style={{ background: '#fef3c7' }}>
              <Ticket size={20} style={{ color: '#f59e0b' }} />
            </div>
          </div>
          <div className="stats-card-value">{stats?.total_redeem_codes || 0}</div>
          <div className="stats-card-change">
            {stats?.unused_codes || 0} unused
          </div>
        </div>

        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">Requests Today</span>
            <div className="stats-card-icon" style={{ background: '#e0e7ff' }}>
              <Activity size={20} style={{ color: '#6366f1' }} />
            </div>
          </div>
          <div className="stats-card-value">{stats?.total_requests_today || 0}</div>
          <div className="stats-card-change positive">
            <TrendingUp size={16} />
            API usage
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2" style={{ marginTop: '2rem' }}>
        <div className="card">
          <div className="card-header">
            <h2>Quick Actions</h2>
            <p>Common administrative tasks</p>
          </div>
          <div className="quick-actions-list">
            <a href="/admin/users" className="quick-action-item">
              <Users size={20} />
              <div>
                <h3>Manage Users</h3>
                <p>View and manage all users</p>
              </div>
            </a>
            <a href="/admin/api-keys" className="quick-action-item">
              <Key size={20} />
              <div>
                <h3>Manage API Keys</h3>
                <p>Create and manage API keys</p>
              </div>
            </a>
            <a href="/admin/redeem-codes" className="quick-action-item">
              <Ticket size={20} />
              <div>
                <h3>Generate Codes</h3>
                <p>Create redeem codes</p>
              </div>
            </a>
            <a href="/admin/analytics" className="quick-action-item">
              <Activity size={20} />
              <div>
                <h3>View Analytics</h3>
                <p>Usage statistics and reports</p>
              </div>
            </a>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2>System Health</h2>
            <p>Monitor system status</p>
          </div>
          <div className="health-list">
            <div className="health-item">
              <div className="health-indicator active"></div>
              <div>
                <h4>API Server</h4>
                <p>Running normally</p>
              </div>
            </div>
            <div className="health-item">
              <div className="health-indicator active"></div>
              <div>
                <h4>Database</h4>
                <p>Connected</p>
              </div>
            </div>
            <div className="health-item">
              <div className="health-indicator active"></div>
              <div>
                <h4>AI Providers</h4>
                <p>All operational</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;